import "./carousel.scss";
import React, { cloneElement, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDeviceCheck, useHorizontalScroll } from "hooks";

import { Dots } from "./Dots";
import { Arrow } from "./Arrow";

const defaultSettingsConst = {
  draggable: true,
  dots: false,
  autoplay: false,
  speed: 500,
  autoplaySpeed: 3000,
  slidesToShow: 1,
  cssEase: "ease",
  gap: 8,
  adaptiveHeight: true,
  height: "100%",
  itemHeight: 534,
  variableWidth: false,
  width: "100%",
  itemWidth: 450,
  arrows: false,
  nextArrow: null,
  prevArrow: null,
  animation: "none",
  center: false,
  defaultSlider: 0,
  responsive: {
    breakpoint: 992,
    settings: {
      draggable: true,
      dots: false,
      autoplay: false,
      speed: 500,
      autoplaySpeed: 3000,
      slidesToShow: 1,
      cssEase: "ease",
      gap: 8,
      adaptiveHeight: true,
      height: 448,
      itemHeight: 448,
      variableWidth: false,
      width: "100%",
      itemWidth: 343,
      arrows: false,
      nextArrow: null,
      prevArrow: null,
      animation: "none",
      center: false,
      defaultSlider: 0,
    },
  },
};

/**
 * Carousel component
 * @param lang
 * @param style
 * @param getActiveSlide
 * @param settings
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */

export const Carousel = ({
  style = {},
  getActiveSlide = (value) => value,
  settings = defaultSettingsConst,
  children,
  lang,
}) => {
  const [defaultSettings, setDefaultSettings] = useState(defaultSettingsConst);
  const { isMobile, width } = useDeviceCheck(
    defaultSettings.responsive.breakpoint
  );
  const {
    scrollContainerRef,
    carouselIsMove,
    setCarouselIsMove,
    carouselNext,
    onMouseMove,
    onMouseUp,
    onMouseDown,
    onMouseLeave,
  } = useHorizontalScroll();
  const [settingsSlider, setSettingsSlider] = useState(defaultSettings);

  const loadSettingSlider = (ds) => {
    if (isMobile) {
      return {
        ...ds.responsive.settings,
      };
    } else {
      return {
        ...ds,
      };
    }
  };

  useEffect(() => {
    const ds = {
      ...defaultSettingsConst,
      ...settings,
      responsive: {
        ...defaultSettingsConst.responsive,
        ...settings.responsive,
        settings: {
          ...defaultSettingsConst.responsive.settings,
          ...settings.responsive.settings,
        },
      },
    };
    setDefaultSettings(ds);
    setSettingsSlider(loadSettingSlider(ds));
  }, [width]); //eslint-disable-line

  const [activeSlide, setActiveSlide] = React.useState(0);

  useEffect(() => {
    if (carouselIsMove) {
      setActiveSlide((prev) => {
        const nextSlider = prev === children.length - 1 ? 0 : prev + 1;
        const prevSlider = prev === 0 ? children.length - 1 : prev - 1;
        const value = carouselNext ? nextSlider : prevSlider;
        getActiveSlide(value);
        return value;
      });
      setCarouselIsMove(false);
    }
  }, [carouselIsMove]); //eslint-disable-line

  settingsSlider.nextArrow = (
    <Arrow
      locale={lang}
      visibility={activeSlide !== children.length - 1}
      handleClick={() =>
        setActiveSlide((prev) => {
          const value = prev === children.length - 1 ? 0 : prev + 1;
          getActiveSlide(value);
          return value;
        })
      }
    />
  );

  settingsSlider.prevArrow = (
    <Arrow
      locale={lang}
      visibility={activeSlide !== 0}
      direction="left"
      handleClick={() =>
        setActiveSlide((prev) => {
          const value = prev === 0 ? children.length - 1 : prev - 1;
          getActiveSlide(value);
          return value;
        })
      }
    />
  );

  useEffect(() => {
    if (settingsSlider.autoplay) {
      const interval = setInterval(() => {
        setActiveSlide((prev) => {
          const value = prev === children.length - 1 ? 0 : prev + 1;
          getActiveSlide(value);
          return value;
        });
      }, settingsSlider.autoplaySpeed);
      return () => clearInterval(interval);
    }
  }, [settingsSlider.autoplay]); // eslint-disable-line

  useEffect(() => {
    if (settingsSlider.defaultSlider !== 0) {
      const containerWidth = window.innerWidth - 16;
      const scrollx = (containerWidth - settingsSlider.itemWidth) / 2;
      scrollContainerRef.current.scrollLeft =
        settingsSlider.defaultSlider *
          (settingsSlider.itemWidth + settingsSlider.gap) -
        scrollx;

      setActiveSlide(settingsSlider.defaultSlider);
      getActiveSlide(settingsSlider.defaultSlider);
    }
  }, [settingsSlider.defaultSlider]); // eslint-disable-line

  const getStyleCarouselHeight = () => {
    if (settingsSlider.adaptiveHeight) {
      return { height: "100%" };
    }
    return {
      height: settingsSlider.height,
    };
  };

  const getStyleCarouselWidth = () => {
    if (settingsSlider.variableWidth) {
      return {
        width: settingsSlider.width,
      };
    }
    return { width: "100%" };
  };

  const getStyleItemWidth = () => {
    return {
      width: settingsSlider.itemWidth,
      minWidth: settingsSlider.itemWidth,
    };
  };

  const getStyleItemHeight = () => {
    return { height: settingsSlider.itemHeight };
  };

  const getStyleGap = () => {
    return { gap: settingsSlider.gap };
  };

  const getStyleIsDot = () => {
    return { width: settingsSlider.dots ? "max-content" : "100%" };
  };

  const itemWidth =
    typeof settingsSlider.itemWidth === "string"
      ? scrollContainerRef.current?.getBoundingClientRect()?.width
      : settingsSlider.itemWidth;

  const getStyleLeftAnimationOpacity = () => {
    if (
      (settingsSlider.animation === "opacity" || settingsSlider.autoplay) &&
      settingsSlider.animation !== "scale"
    ) {
      return {
        transition: `opacity ${settingsSlider.speed}ms ${settingsSlider.cssEase}, visibility ${settingsSlider.speed}ms ${settingsSlider.cssEase}`,
      };
    } else return {};
  };

  const getStyleAnimationScaleItem = (index) => {
    if (settingsSlider.animation === "scale") {
      return {
        transform: index === activeSlide ? "scale(1)" : "scale(0.6)",
        transition: `all ${settingsSlider.speed}ms ${settingsSlider.cssEase}`,
      };
    } else return {};
  };

  const getStyleAnimationScale = () => {
    if (settingsSlider.animation === "scale") {
      return {
        transition: `transform ${settingsSlider.speed}ms ${settingsSlider.cssEase}`,
      };
    } else return {};
  };

  const getStyleOpacity = (index) => {
    if (
      settingsSlider.animation === "opacity" ||
      settingsSlider.animation === "scale" ||
      settingsSlider.dots
    ) {
      if (index === activeSlide) {
        return { opacity: 1 };
      } else return { opacity: 0 };
    } else return {};
  };

  const getStyleCenter = () => {
    if (settingsSlider.center) {
      return {
        width: "100%",
        display: "flex",
        alignItems: "center",
      };
    } else return {};
  };

  const getStyleArrow = () => {
    if (settingsSlider.arrows) {
      return {
        gap: settingsSlider.gap,
        display: "grid",
        gridTemplateRows: "repeat(1, 1fr)",
        gridTemplateColumns: "repeat(12, 1fr)",
      };
    }
  };

  const getStyleCarouselArrow = () => {
    if (settingsSlider.arrows) {
      return {
        gridColumn: "auto / span 10",
      };
    }
  };

  return (
    <div
      className={`carousel__container`}
      style={{
        ...getStyleIsDot(),
        ...getStyleCenter(),
        ...getStyleArrow(),
        ...style,
      }}
    >
      {settingsSlider.arrows ? settingsSlider.prevArrow : null}
      <div
        data-testid="carousel"
        className={`carousel${
          settingsSlider.arrows ? " carousel--arrows" : ""
        }${
          settingsSlider.animation === "scale" ||
          settingsSlider.animation === "opacity"
            ? " carousel--animation"
            : ""
        }`}
        ref={scrollContainerRef}
        onMouseMove={settingsSlider.draggable ? onMouseMove : null}
        onMouseUp={settingsSlider.draggable ? onMouseUp : null}
        onMouseDown={settingsSlider.draggable ? onMouseDown : null}
        onMouseLeave={settingsSlider.draggable ? onMouseLeave : null}
        onTouchStart={settingsSlider.draggable ? onMouseDown : null}
        onTouchEnd={settingsSlider.draggable ? onMouseUp : null}
        style={{
          ...getStyleCarouselHeight(),
          ...getStyleCarouselWidth(),
          ...getStyleGap(),
          ...getStyleCarouselArrow(),
          ...getStyleAnimationScale(),
        }}
        draggable={settingsSlider.draggable}
        onDragStart={(e) => {
          e.preventDefault();
          return false;
        }}
      >
        {React.Children.map(children, (child, index) => {
          return (
            <div
              data-testid={`carousel__item-${index}`}
              key={index}
              className={`carousel__item carousel__item-${index}`}
              style={{
                ...getStyleItemHeight(),
                ...getStyleItemWidth(),
                ...getStyleLeftAnimationOpacity(),
                ...getStyleAnimationScaleItem(index),
                ...getStyleOpacity(index),
              }}
            >
              {cloneElement(child)}
            </div>
          );
        })}
      </div>
      {settingsSlider.arrows ? settingsSlider.nextArrow : null}
      {settingsSlider.dots ? (
        <Dots locale={lang} activeSlide={activeSlide} onClick={setActiveSlide}>
          {children}
        </Dots>
      ) : null}
    </div>
  );
};

Carousel.propTypes = {
  lang: PropTypes.string,
  style: PropTypes.object,
  settings: PropTypes.shape({
    draggable: PropTypes.bool,
    dots: PropTypes.bool,
    autoplay: PropTypes.bool,
    speed: PropTypes.number,
    autoplaySpeed: PropTypes.number,
    slidesToShow: PropTypes.number,
    cssEase: PropTypes.string,
    gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    adaptiveHeight: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    itemHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    variableWidth: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    itemWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    arrows: PropTypes.bool,
    nextArrow: PropTypes.node,
    prevArrow: PropTypes.node,
    animation: PropTypes.oneOf(["opacity", "scale", "none"]),
    center: PropTypes.bool,
    defaultSlider: PropTypes.number,
    responsive: PropTypes.shape({
      breakpoint: PropTypes.number,
      settings: PropTypes.shape({
        draggable: PropTypes.bool,
        autoplay: PropTypes.bool,
        speed: PropTypes.number,
        autoplaySpeed: PropTypes.number,
        slidesToShow: PropTypes.number,
        cssEase: PropTypes.string,
        gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        adaptiveHeight: PropTypes.bool,
        height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        itemHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        variableWidth: PropTypes.bool,
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        itemWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        animation: PropTypes.oneOf(["opacity", "scale", "none"]),
        center: PropTypes.bool,
        defaultSlider: PropTypes.number,
      }),
    }),
  }),
  getActiveSlide: PropTypes.func,
  children: PropTypes.node.isRequired,
};
